/* eslint-disable camelcase */
import { useState, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import {
  Card,
  Table,
  Stack,
  TableRow,
  TableBody,
  TableCell,
  Container,
  Typography,
  TableContainer,
  TablePagination,
  TableHead,
  TextField,
  InputAdornment,
  Button,
  Box,
  CircularProgress,
} from '@mui/material';
import axios from 'axios';
// components
import Page from '../components/Page';
import Scrollbar from '../components/Scrollbar';
import Iconify from '../components/Iconify';
import ModernMuiTable from '../components/ModernMuiTable';
import useAxiosWithAuth from '../api/useAxiosWithAuth';

const WarehouseList = () => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [warehouses, setWarehouses] = useState([]);
  const [paginationMeta, setPaginationMeta] = useState({ total: 0 });
  const [isLoading, setIsLoading] = useState(true);
  const [isExporting, setIsExporting] = useState(false);
  const [togglingWarehouse, setTogglingWarehouse] = useState(null); // Track which warehouse is being toggled

  const { get } = useAxiosWithAuth();
  const { getIdTokenClaims } = useAuth0();

  // Fetch data from API
  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        const response = await get(`/supplier/warehouses?page_size=${rowsPerPage}&page_number=${page + 1}`);

        if (response.data) {
          setWarehouses(response.data.Items || []);
          setPaginationMeta({
            total: response.data.Pagination?.NumberOfItems || 0,
          });
        } else {
          setWarehouses([]);
          setPaginationMeta({ total: 0 });
        }
      } catch (error) {
        console.error('Error fetching warehouses:', error);
        setWarehouses([]);
        setPaginationMeta({ total: 0 });
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [page, rowsPerPage]);

  // Toggle AttachOnReports for a specific warehouse
  const toggleAttachOnReports = async (warehouseCode, newValue) => {
    try {
      // Set the toggling state to prevent multiple clicks
      setTogglingWarehouse(warehouseCode);

      // Get the authentication token
      const tokenClaims = await getIdTokenClaims();
      const accessToken = tokenClaims?.__raw;

      const response = await axios.put(
        `${process.env.REACT_APP_ORDER_API_END_PONT}/api/unleashed/warehouses/${warehouseCode}`,
        { toggle_value: newValue },
        {
          headers: {
            Authorization: `Token ${accessToken}`,
            'Content-Type': 'application/json',
          },
        }
      );

      // Refresh the data to reflect the updated value
      const refreshResponse = await get(`/supplier/warehouses?page_size=${rowsPerPage}&page_number=${page + 1}`);

      if (refreshResponse.data) {
        setWarehouses(refreshResponse.data.Items || []);
        setPaginationMeta({
          total: refreshResponse.data.Pagination?.NumberOfItems || 0,
        });
      } else {
        setWarehouses([]);
        setPaginationMeta({ total: 0 });
      }
    } catch (error) {
      console.error('Error toggling AttachOnReports:', error);
    } finally {
      // Reset the toggling state
      setTogglingWarehouse(null);
    }
  };

  // Pagination
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0); // Reset to first page when changing rows per page
  };

  // Function to export data as CSV
  const exportToCSV = async () => {
    try {
      setIsExporting(true); // Set export loading state

      // Fetch all data with a large page size
      const response = await get(`/supplier/warehouses?page_size=9999999&page_number=1`);

      const allWarehouses = response.data.Items || [];

      if (allWarehouses.length === 0) {
        // If no data is available, just return
        return;
      }

      // Define CSV headers
      const headers = [
        'Warehouse Code',
        'Warehouse Name',
        'Address',
        'City',
        'Region',
        'Country',
        'Post Code',
        'Phone Number',
        'Contact Name',
        'Is Default',
        'Obsolete',
        'Attach On Reports',
      ];

      // Create CSV content
      const csvContent = [
        headers.join(','),
        ...allWarehouses.map((warehouse) =>
          [
            warehouse.WarehouseCode ? `"${warehouse.WarehouseCode}"` : '""',
            warehouse.WarehouseName ? `"${warehouse.WarehouseName}"` : '""',
            `"${[warehouse.AddressLine1, warehouse.Suburb].filter(Boolean).join(', ')}"`,
            warehouse.City ? `"${warehouse.City}"` : '""',
            warehouse.Region ? `"${warehouse.Region}"` : '""',
            warehouse.Country ? `"${warehouse.Country}"` : '""',
            warehouse.PostCode ? `"${warehouse.PostCode}"` : '""',
            warehouse.PhoneNumber ? `"${warehouse.PhoneNumber}"` : '""',
            warehouse.ContactName ? `"${warehouse.ContactName}"` : '""',
            warehouse.IsDefault ? `"${warehouse.IsDefault}"` : '""',
            warehouse.Obsolete ? `"${warehouse.Obsolete}"` : '""',
            warehouse.AttachOnReports !== undefined ? `"${warehouse.AttachOnReports ? 'Yes' : 'No'}"` : '""',
          ].join(',')
        ),
      ].join('\n');

      // Create and download the CSV file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.setAttribute('href', url);
      link.setAttribute('download', `warehouse-list-${new Date().toISOString().slice(0, 10)}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error exporting CSV:', error);
    } finally {
      setIsExporting(false); // Reset export loading state
    }
  };

  return (
    <Page title="Warehouse List">
      <Container maxWidth="xl">
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2} ml={2}>
          <Typography variant="h4" gutterBottom>
            Warehouse List
          </Typography>
        </Stack>

        <Card>
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'center',
              flexWrap: 'wrap',
              padding: '16px',
            }}
          >
            <Button
              variant="outlined"
              onClick={exportToCSV}
              startIcon={isExporting ? null : <Iconify icon="solar:export-bold" />}
              style={{ marginBottom: '16px', height: '40px' }}
              disabled={isLoading || isExporting} // Disable when loading or exporting
            >
              {isExporting ? 'Exporting...' : 'Export CSV'}
            </Button>
          </div>

          <ModernMuiTable
            containerSx={{
              minWidth: 800,
              '& .MuiTableCell-root': {
                whiteSpace: 'nowrap',
              },
            }}
          >
            <TableHead>
                <TableRow>
                  <TableCell>Warehouse Code</TableCell>
                  <TableCell>Warehouse Name</TableCell>
                  <TableCell>Address</TableCell>
                  <TableCell>City</TableCell>
                  <TableCell>Region</TableCell>
                  <TableCell>Country</TableCell>
                  <TableCell>Post Code</TableCell>
                  <TableCell>Phone Number</TableCell>
                  <TableCell>Contact Name</TableCell>
                  <TableCell>Is Default</TableCell>
                  <TableCell>Obsolete</TableCell>
                  <TableCell
                      sx={{
                        position: 'sticky',
                        right: 0,
                        background: (theme) => theme.palette.background.paper,
                        zIndex: 10, // Higher z-index for header
                        borderLeft: '1px solid',
                        borderColor: 'divider',
                      }}
                    >
                      Attach On Reports
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {isLoading ? (
                    <TableRow>
                      <TableCell colSpan={12} align="center">
                        <Box display="flex" justifyContent="center" alignItems="center" p={4}>
                          <CircularProgress size={48} />
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : (
                    <>
                      {warehouses.length > 0 ? (
                        warehouses.map((warehouse) => (
                          <TableRow key={warehouse.Guid}>
                            <TableCell>{warehouse.WarehouseCode}</TableCell>
                            <TableCell>{warehouse.WarehouseName}</TableCell>
                            <TableCell>
                              {[warehouse.AddressLine1, warehouse.Suburb].filter(Boolean).join(', ')}
                            </TableCell>
                            <TableCell>{warehouse.City}</TableCell>
                            <TableCell>{warehouse.Region}</TableCell>
                            <TableCell>{warehouse.Country}</TableCell>
                            <TableCell>{warehouse.PostCode}</TableCell>
                            <TableCell>{warehouse.PhoneNumber || ''}</TableCell>
                            <TableCell>{warehouse.ContactName || ''}</TableCell>
                            <TableCell>{warehouse.IsDefault ? 'Yes' : 'No'}</TableCell>
                            <TableCell>{warehouse.Obsolete ? 'Yes' : 'No'}</TableCell>
                            <TableCell
                              sx={{
                                position: 'sticky',
                                right: 0,
                                background: (theme) => theme.palette.background.paper,
                                boxShadow: '0px 0px 5px 0px rgba(0,0,0,0.1)',
                                zIndex: 1,
                                borderLeft: '1px solid',
                                borderColor: 'divider',
                              }}
                            >
                              {(() => {
                                const isToggling = togglingWarehouse === warehouse.WarehouseCode;
                                const isOn = warehouse.AttachOnReports;

                                let buttonText;
                                if (isToggling) {
                                  buttonText = <CircularProgress size={20} />;
                                } else if (isOn) {
                                  buttonText = 'ON';
                                } else {
                                  buttonText = 'OFF';
                                }

                                return (
                                  <Button
                                    variant={isOn ? 'contained' : 'outlined'}
                                    color={isOn ? 'success' : 'inherit'}
                                    size="small"
                                    onClick={() => toggleAttachOnReports(warehouse.WarehouseCode, !isOn)}
                                    disabled={isToggling}
                                  >
                                    {buttonText}
                                  </Button>
                                );
                              })()}
                            </TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell colSpan={12} align="center">
                            No warehouses found
                          </TableCell>
                        </TableRow>
                      )}
                    </>
                  )}
                </TableBody>
              </ModernMuiTable>

          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={paginationMeta.total}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Card>
      </Container>
    </Page>
  );
};

export default WarehouseList;
